import { useState, useEffect, useCallback, useRef } from "react";
import roomStatsService from "../services/rooms/roomStatsService";

/**
 * Custom hook for fetching room preview/stats data
 * @param {String} roomId - The room ID
 * @param {Object} options - Hook options
 * @param {Boolean} options.compact - Use compact preview (default: false)
 * @param {Boolean} options.autoRefresh - Enable auto refresh (default: false)
 * @param {Number} options.refreshInterval - Refresh interval in ms (default: 300000 - 5min)
 */
export const useRoomPreview = (roomId, options = {}) => {
  const {
    compact = false,
    autoRefresh = false,
    refreshInterval = 300000,
  } = options;

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const mountedRef = useRef(true);
  const intervalRef = useRef(null);

  /**
   * Fetch preview data
   */
  const fetchPreview = useCallback(async () => {
    if (!roomId) {
      setLoading(false);
      return;
    }

    try {
      setError(null);

      const response = compact
        ? await roomStatsService.getCompactPreview(roomId)
        : await roomStatsService.getFullPreview(roomId);

      if (mountedRef.current) {
        if (response.success) {
          setPreview(response.data);
        } else {
          setError(response.message || "Failed to load preview");
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || "Failed to load preview");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [roomId, compact]);

  /**
   * Force refresh stats
   */
  const refresh = useCallback(async () => {
    if (!roomId) return;

    try {
      setIsRefreshing(true);
      setError(null);

      const response = await roomStatsService.refreshStats(roomId);

      if (mountedRef.current) {
        if (response.success) {
          setPreview(response.data);
        }
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message || "Failed to refresh");
      }
    } finally {
      if (mountedRef.current) {
        setIsRefreshing(false);
      }
    }
  }, [roomId]);

  /**
   * Refetch preview data
   */
  const refetch = useCallback(async () => {
    setLoading(true);
    await fetchPreview();
  }, [fetchPreview]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchPreview();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchPreview]);

  // Auto refresh
  useEffect(() => {
    if (!autoRefresh || !roomId) return;

    intervalRef.current = setInterval(fetchPreview, refreshInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchPreview, roomId]);

  return {
    preview,
    loading,
    error,
    isRefreshing,
    refresh,
    refetch,
  };
};

/**
 * Hook for fetching specific stats
 */
export const useRoomStats = (roomId, statType = "activity") => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!roomId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let response;
      switch (statType) {
        case "activity":
          response = await roomStatsService.getActivityStats(roomId);
          break;
        case "tasks":
          response = await roomStatsService.getTaskStats(roomId);
          break;
        case "engagement":
          response = await roomStatsService.getEngagementStats(roomId);
          break;
        case "health":
          response = await roomStatsService.getHealthScore(roomId);
          break;
        default:
          response = await roomStatsService.getActivityStats(roomId);
      }

      if (response.success) {
        setStats(response.data);
      } else {
        setError(response.message || "Failed to load stats");
      }
    } catch (err) {
      setError(err.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  }, [roomId, statType]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};

export default useRoomPreview;
